import React, { useRef, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

function insertNullGaps(data, expectedIntervalMs, factor) {
  expectedIntervalMs = expectedIntervalMs || 60 * 60 * 1000; // hourly
  factor = factor || 1; // break if > 1.5x interval
  if (!data || !data.length) return data;

  var threshold = expectedIntervalMs * factor;
  var out = [data[0]];
  for (var i = 1; i < data.length; i++) {
    var prevX = out[out.length - 1][0];
    var x = data[i][0];
    var y = data[i][1];
    if (x - prevX > threshold) out.push([prevX + 1, null]); // force a break
    out.push([x, y]);
  }
  return out;
}

export default function SystemTempChart({
  title,
  data,
  xAxis,
  colors,
  height = 200,
  loading = false,
}) {
  const chartRef = useRef(null);

  React.useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current.chart;
      if (loading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [loading]);

  const options = {
    chart: {
      height,
      zooming: { type: 'x' },
      panning: true,
      panKey: 'shift',
      events: {
        load: function () {
          const chart = this;
          chart.customValueBox = chart.renderer
            .html('', chart.chartWidth, 10)
            .css({
              fontSize: '16px',
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'row',
              marginRight: '10px',
            })
            .add();
        },
        redraw: function () {
          if (this.customValueBox) {
            this.customValueBox.attr({
              x: this.chartWidth - this.chartWidth / 3,
              y: 20,
            });
          }
        },
      },
    },
    lang: { noData: 'No data found in this range' },
    time: { timezone: 'Europe/Copenhagen' },
    credits: { enabled: false },
    legend: { enabled: true },
    title: {
      text: title || 'System Temperatures',
      align: 'left',
      style: { fontSize: '14px' },
    },
    xAxis,
    yAxis: { title: { text: '°C' } },
    tooltip: {
      shared: true,
      formatter: function () {
        const t = this.series.chart.time; // <- use chart time, not Highcharts.dateFormat
        let s = `<b>${t.dateFormat('%Y-%m-%d %H:%M', this.x)}</b><br/>`;
        this.points.forEach((point) => {
          const unit = point.series.userOptions.unitLabel || '';
          s += `<span style="color:${point.color}">●</span> ${
            point.series.name
          }: <b>${point.y.toFixed(2)} ${unit}</b><br/>`;
        });
        return s;
      },
    },
    series: data.map((series, i) => ({
      name: series.name,
      category: series.category,
      data: insertNullGaps(series.data, 60 * 60 * 1000, 2), // insert nulls if gap > 2 hours
      unitLabel: series.unitLabel || '°C',
      type: 'line',
      color: colors?.[i] || undefined, // Use custom color if provided
      states: {
        hover: {
          lineWidthPlus: 0, // prevent stroke width from increasing on hover
        },
      },
    })),
  };

  useEffect(() => {
    if (chartRef.current && chartRef.current.chart) {
      chartRef.current.chart.redraw();
    }
  }, [data]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartRef}
      immutable={false}
    />
  );
}
